import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import PropTypes from "prop-types";
import Form from "./Form";
import ConfirmArchiveModal from "../../modals/ConfirmArchiveModal";

const BoxFormTabbed = props => {
  const [tab, setTab] = useState("0");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notAllowedToRemove, setNotAllowedToRemove] = useState(false);
  const [checkingRemoval, setCheckingRemoval] = useState(typeof props.object.id === "number");
  const [removalError, setRemovalError] = useState(false);
  const {
    fields,
    tabs,
    name,
    removable,
    object,
    handleChange,
    handleRemove,
    removeAfterSaved
  } = props;

  const canRemove = () => {
    return (
      removeAfterSaved || (!removeAfterSaved && typeof object.id !== "number")
    );
  };

  useEffect(() => {
    let cancelled = false;
    const checkEnrollments = async () => {
      setCheckingRemoval(typeof object.id === "number");
      setNotAllowedToRemove(false);
      setRemovalError(false);
      if (typeof object.id !== "number") return;
      try {
        for (const kind of ["programs", "virtual", "events"]) {
          const response = await axios.get(
            `${process.env.REACT_APP_API}/partners/${kind}/enrollments/count/child/${object.id}`
          );
          const count = Number(response.data.data[0].count);
          if (!Number.isFinite(count)) throw new Error("Invalid enrollment count");
          if (count > 0) {
            if (!cancelled) setNotAllowedToRemove(true);
            return;
          }
        }
      } catch (error) {
        if (!cancelled) {
          setNotAllowedToRemove(true);
          setRemovalError(true);
        }
      } finally {
        if (!cancelled) setCheckingRemoval(false);
      }
    };
    checkEnrollments();
    return () => { cancelled = true; };
  }, [object.id]);

  const showConfirmationModal = () => {
    if (!showConfirmModal) {
      return null;
    }

    const confirmationModal = (
      <ConfirmArchiveModal
        onSubmit={_ => handleRemove(object)}
        onClose={_ => setShowConfirmModal(false)}
        modalBody={
          removalError
            ? "We could not check this child's enrollments. Please close and reopen their profile to try again."
            : notAllowedToRemove
            ? "Sorry, children that have enrolled or applied into a program cannot be deleted."
            : ""
        }
        notAllowed={notAllowedToRemove}
        toRemove="child"
        toArchive={true}
      />
    );

    // A transformed form card becomes the containing block for fixed children,
    // which offsets the modal overlay. Render it at the dashboard level so the
    // shared fixed overlay remains centered against the viewport.
    const dashboardRoot = document.querySelector(".main-pages");
    return dashboardRoot
      ? createPortal(confirmationModal, dashboardRoot)
      : confirmationModal;
  };

  return (
    <>
    {showConfirmationModal()}
    <div className="box">
      <div className="bgofbox">
        <div className="header">
          <div className="row">
            <div className="col-4">
              <h2>{name}</h2>
            </div>
            <div className="col-8 d-flex">
              {tabs.map((t, i) => (
                <button
                  key={t.name}
                  className={parseInt(i) === parseInt(tab) ? "active" : ""}
                  onClick={() => setTab(i.toString())}
                >
                  {t.name}
                </button>
              ))}
              {removable && canRemove() ? (
                <>
                  <span className="spacer"></span>
                  <button
                    className="remove-box"
                    type="button"
                    aria-label={checkingRemoval ? "Checking child enrollments" : "Remove child"}
                    disabled={checkingRemoval}
                    onClick={() => { 
                      setShowConfirmModal(true);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
        {Object.keys(fields).map(fieldTab => {
          return fields[fieldTab].customForm ? (
            fields[fieldTab].customForm
          ) : fieldTab === tab ? (
            <Form
              key={fieldTab}
              handleChange={handleChange}
              fields={fields[fieldTab]}
            />
          ) : (
            ""
          );
        })}
      </div>
    </div>
    </>
  );
};

BoxFormTabbed.propTypes = {
  tabs: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  removable: PropTypes.bool,
  handleRemove: PropTypes.func,
  object: PropTypes.object,
  removeAfterSaved: PropTypes.bool
};

BoxFormTabbed.defaultProps = {
  removable: false,
  handleRemove: () => false,
  object: {},
  removeAfterSaved: true
};

export default BoxFormTabbed;
